import React, { useMemo, useState } from 'react';
import { Box, Typography, Tooltip, Paper, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const getStatusInfo = (status, theme) => {
    const styleMap = {
        complete: { Icon: CheckBoxIcon, style: { color: theme.palette.success.dark }, label: "Complete" },
        missed: { Icon: FiberManualRecordIcon, style: { color: theme.palette.warning.main, fontSize: '10px' }, label: "Missed" },
        out_of_range: { Icon: WarningAmberIcon, style: { color: theme.palette.error.main }, label: "Out of Range / Alert" },
        not_assigned: { Icon: CheckBoxOutlineBlankIcon, style: { color: theme.palette.grey[400] }, label: "Not Tracked" },
    };
    return styleMap[status] || styleMap.not_assigned;
};

const LegendItem = ({ icon, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
        {icon}
        <Typography variant="caption">{label}</Typography>
    </Box>
);

const CHECKIN_LABELS = { 'vitals': 'Vitals', 'medication_adherence_check': 'Medication', 'mood': 'Mood/Feeling', 'symptoms_feelings': 'Symptoms/Pain', 'diet_fluid': 'Diet/Fluids/GI', 'activity': 'Activity/Exertion' };

const ClinicianMiniCalendar = ({ calendarData }) => {
    const theme = useTheme();
    const [viewOffset, setViewOffset] = useState(0);

    const weeklyData = useMemo(() => {
        if (!Array.isArray(calendarData)) return [];
        const totalDays = calendarData.length;
        const end = totalDays - viewOffset;
        const start = Math.max(0, end - 7);
        return calendarData.slice(start, end);
    }, [calendarData, viewOffset]);

    const pivotedData = useMemo(() => {
        if (weeklyData.length === 0) return {};
        const dataByLogType = {};
        Object.keys(CHECKIN_LABELS).forEach(key => dataByLogType[key] = []);
        weeklyData.forEach((day) => {
            const dailyStatuses = new Map((day.checkin_statuses || []).map(s => [s.log_type, s.status]));
            Object.keys(CHECKIN_LABELS).forEach(logType => {
                const status = dailyStatuses.get(logType) || 'not_assigned';
                dataByLogType[logType].push({ date: day.date, status: status });
            });
        });
        return dataByLogType;
    }, [weeklyData]);

    const monthRowContent = useMemo(() => {
        if (!weeklyData || weeklyData.length === 0) return [];
        const content = Array(7).fill(null);
        let lastMonth = -1;
        weeklyData.forEach(({ date }, index) => {
            const currentDate = new Date(date.replace(/-/g, '/'));
            const currentMonth = currentDate.getMonth();
            if (currentMonth !== lastMonth) {
                content[index] = currentDate.toLocaleDateString('en-US', { month: 'short' });
                lastMonth = currentMonth;
            }
        });
        return content.slice(0, weeklyData.length);
    }, [weeklyData]);

    if (!Array.isArray(calendarData) || calendarData.length === 0) {
        return (
            <Paper elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 2.5, textAlign: 'center' }}>
                 <Typography variant="body2" color="text.secondary">Adherence data is not yet available.</Typography>
            </Paper>
        );
    }
    
    if (weeklyData.length === 0) {
        return (
             <Paper elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 2.5, textAlign: 'center' }}>
                 <Typography variant="body2" color="text.secondary">No data for this date range.</Typography>
            </Paper>
        );
    }
    
    const firstDay = new Date(weeklyData[0].date.replace(/-/g, '/'));
    const lastDay = new Date(weeklyData[weeklyData.length - 1].date.replace(/-/g, '/'));
    const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const maxOffset = Math.max(0, calendarData.length - 7);

    return (
        <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'text.secondary', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    Weekly Adherence
                </Typography>
                 <Box sx={{ display: 'flex', flexWrap: 'wrap', color: 'text.secondary' }}>
                    <LegendItem icon={<CheckBoxIcon sx={{ color: 'success.dark', fontSize: '1.1rem', mr: 0.5 }} />} label="Complete" />
                    <LegendItem icon={<WarningAmberIcon sx={{ color: 'error.main', fontSize: '1.1rem', mr: 0.5 }} />} label="Alert" />
                    <LegendItem icon={<FiberManualRecordIcon sx={{ color: 'warning.main', fontSize: '0.7rem', mr: 0.5, ml: 0.2 }} />} label="Missed" />
                    <LegendItem icon={<CheckBoxOutlineBlankIcon sx={{ color: 'grey.400', fontSize: '1.1rem', mr: 0.5 }} />} label="Not Tracked" />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', mb: 0.5 }}>
                <Box sx={{ width: '120px', minWidth: '120px', pr: 1.5 }} />
                <Box sx={{ display: 'flex', gap: '6px', width: '100%', justifyContent: 'space-around' }}>
                    {monthRowContent.map((month, index) => (
                        <Box key={`month-${index}`} sx={{ width: '24px', height: '16px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                            {month && <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', lineHeight: 1 }}>{month}</Typography>}
                        </Box>
                    ))}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ width: '120px', minWidth: '120px', pr: 1.5 }} />
                <Box sx={{ display: 'flex', gap: '6px', width: '100%', justifyContent: 'space-around' }}>
                    {weeklyData.map(({ date }) => (
                        <Box key={`day-${date}`} sx={{ width: '24px', display: 'flex', justifyContent: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                {new Date(date.replace(/-/g, '/')).getDate()}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {Object.entries(pivotedData).map(([logType, statuses]) => (
                    <Box key={logType} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" align="right" sx={{ fontWeight: 500, width: '120px', minWidth: '120px', pr: 1.5, color: 'text.secondary' }}>
                            {CHECKIN_LABELS[logType] || logType}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: '6px', width: '100%', justifyContent: 'space-around' }}>
                            {statuses.map(({ date, status }) => {
                                const statusInfo = getStatusInfo(status, theme);
                                return(
                                <Tooltip key={`${logType}-${date}`} title={`${CHECKIN_LABELS[logType]}: ${statusInfo.label} on ${new Date(date.replace(/-/g, '/')).toLocaleDateString()}`}>
                                    <Box sx={{ width: '24px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <statusInfo.Icon sx={{ ...statusInfo.style, fontSize: '1.2rem' }} />
                                    </Box>
                                </Tooltip>
                                );
                            })}
                        </Box>
                    </Box>
                ))}
            </Box>
            
             <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mt: 2, justifyContent: 'flex-end' }}>
                <IconButton size="small" onClick={() => setViewOffset(prev => Math.min(prev + 7, maxOffset))} disabled={viewOffset >= maxOffset}>
                    <ChevronLeftIcon />
                </IconButton>
                <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayOutlinedIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                    {`Showing: ${formatDate(firstDay)} → ${formatDate(lastDay)}`}
                </Typography>
                 <IconButton size="small" onClick={() => setViewOffset(prev => Math.max(0, prev - 7))} disabled={viewOffset === 0}>
                    <ChevronRightIcon />
                </IconButton>
            </Box>
        </Paper>
    );
};

export default ClinicianMiniCalendar;